import axios from 'axios';
import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import { getSyncState, listRecords, saveSyncState } from '@/services/localDb';
import { getOrCreateDeviceId } from '@/services/localDb/device';
import { getActiveRestaurantContext } from '@/services/offline/restaurantOfflineData';
import {
  getPendingActionStats,
  listPendingActionConflicts,
  listPendingActions,
  markPendingActionCompleted,
  markPendingActionFailed,
  markPendingActionPending,
  markPendingActionProcessing,
  PENDING_ACTION_STATUS,
  recoverStaleProcessingActions,
} from '@/services/offline/pendingActionQueue';
import { markLocalOrderLineSynced, markLocalOrderSynced, markLocalPaymentSynced, markLocalServiceSynced } from '@/services/offline/offlineOrderService';
import { isBackendUnavailableError, markBackendAvailable, markBackendUnavailable } from '@/services/connectivity/connectivityService';
import { readCachedOperationalPolicy } from '@/services/device/deviceOperationalPolicy';

const SYNC_ENDPOINT = '/v1/api/tpv/offline-sync';
const SYNC_STATE_KEY = 'offline_sync:last_status';

let syncInProgress = false;

export function isPendingActionSyncRunning() {
  return syncInProgress;
}

function nowIso() {
  return new Date().toISOString();
}

function apiBaseUrl() {
  return process.env.VUE_APP_API_URL || '';
}

function normalizeId(value) {
  return value === undefined || value === null || value === '' ? null : String(value);
}

function getActionPayload(action) {
  return action?.payload || {};
}

function getActionOrderLocalId(action) {
  const payload = getActionPayload(action);
  return normalizeId(
    payload.order_local_id
    || payload.local_id
    || (action?.entity_type === 'order' ? action?.entity_local_id : null)
  );
}

function hasResolvedServerTarget(action) {
  const payload = getActionPayload(action);
  return Boolean(
    normalizeId(action?.entity_server_id)
    || normalizeId(payload.server_id)
    || normalizeId(payload.order_server_id)
    || normalizeId(payload.service_server_id)
    || normalizeId(payload.shopping_cart_id)
  );
}

function buildSyncableActions(actions = []) {
  const confirmedOrderLocalIds = new Set(actions
    .filter((action) => action.action_type === 'confirm_order')
    .map((action) => normalizeId(action.entity_local_id || getActionPayload(action).local_id))
    .filter(Boolean));

  const deferredDraftActionIds = new Set();
  const syncableActions = actions.filter((action) => {
    const orderLocalId = getActionOrderLocalId(action);

    if (!orderLocalId) {
      return true;
    }

    if ([
      'create_order',
      'create_order_line',
      'update_order_line',
      'delete_order_line',
    ].includes(action.action_type)) {
      const allowed = confirmedOrderLocalIds.has(orderLocalId);
      if (!allowed) deferredDraftActionIds.add(action.id);
      return allowed;
    }

    if ([
      'update_order_status',
      'update_order_line_status',
      'create_payment_placeholder',
      'cancel_order',
    ].includes(action.action_type)) {
      const allowed = hasResolvedServerTarget(action) || confirmedOrderLocalIds.has(orderLocalId);
      if (!allowed) deferredDraftActionIds.add(action.id);
      return allowed;
    }

    return true;
  });

  return {
    syncableActions,
    deferredDraftActionIds: [...deferredDraftActionIds],
  };
}

function authToken() { return getPartnerToken(); }

function authHeaders() {
  const token = authToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function saveSyncStatus(status) {
  await saveSyncState(SYNC_STATE_KEY, {
    ...status,
    updated_at: nowIso(),
  });
}

function normalizeActionError(error) {
  if (!error) {
    return {
      message: 'Error pendiente de sincronizacion.',
      type: null,
      code: null,
      conflict_payload: null,
    };
  }

  if (typeof error === 'string') {
    return {
      message: error,
      type: null,
      code: null,
      conflict_payload: null,
    };
  }

  return {
    message: error.message || 'Error pendiente de sincronizacion.',
    type: error.type || null,
    code: error.code || null,
    conflict_payload: error.conflict_payload || null,
  };
}

function countActionErrors(actionErrors = {}) {
  return Object.values(actionErrors).reduce((summary, error) => {
    const normalized = normalizeActionError(error);
    summary.failed += 1;
    if (["conflict", "policy"].includes(normalized.type)) {
      summary.conflicts += 1;
    }
    if (normalized.type === 'policy') {
      summary.policy_blocked += 1;
    }
    return summary;
  }, {
    failed: 0,
    conflicts: 0,
    policy_blocked: 0,
  });
}

export async function getLastPendingActionSyncStatus() {
  return getSyncState(SYNC_STATE_KEY);
}

export async function recoverPendingActionLeases() {
  const context = await getActiveRestaurantContext();
  if (!context.restaurant_id) {
    throw new Error(context.error || 'No hay restaurante activo para recuperar acciones pendientes.');
  }

  const processingOwner = `${getOrCreateDeviceId()}:offline-recovery`;
  const recovered = await recoverStaleProcessingActions(context.restaurant_id, {
    processingOwner,
  });

  await saveSyncStatus({
    restaurant_id: context.restaurant_id,
    status: recovered.recovered ? 'recovered_processing' : 'idle',
    message: recovered.recovered
      ? `Se recuperaron ${recovered.recovered} accion(es) atascadas en procesamiento.`
      : 'No habia acciones atascadas en procesamiento.',
    completed: 0,
    failed: 0,
    recovered_processing: recovered.recovered,
    deferred_draft_actions: 0,
  });

  return recovered;
}

export async function getPendingActionSyncDiagnostics() {
  const context = await getActiveRestaurantContext();
  if (!context.restaurant_id) {
    throw new Error(context.error || 'No hay restaurante activo para exportar diagnostico offline.');
  }

  const [
    stats,
    conflicts,
    actions,
    lastSyncStatus,
    syncLogs,
  ] = await Promise.all([
    getPendingActionStats(context.restaurant_id),
    listPendingActionConflicts(context.restaurant_id),
    listPendingActions(context.restaurant_id),
    getLastPendingActionSyncStatus(),
    listRecords('sync_logs', { limit: 50 }),
  ]);

  return {
    generated_at: nowIso(),
    restaurant_id: context.restaurant_id,
    device_id: getOrCreateDeviceId(),
    pending_action_stats: stats,
    conflict_actions: conflicts,
    pending_actions: actions,
    last_sync_status: lastSyncStatus,
    sync_logs: syncLogs.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
  };
}

export async function runPendingActionSync() {
  if (readCachedOperationalPolicy()?.continuityPolicy === 'HYBRID_CLOUD_FIRST') {
    return {
      status: 'disabled_by_cloud_first_policy',
      completed: 0,
      failed: 0,
    };
  }

  if (syncInProgress) {
    return {
      status: 'already_running',
      completed: 0,
      failed: 0,
    };
  }

  syncInProgress = true;
  let context = null;
  let recovered = { recovered: 0 };
  let actions = [];
  let deferredDraftActionIds = [];
  try {
    context = await getActiveRestaurantContext({ preferCached: true });
    const processingOwner = `${getOrCreateDeviceId()}:offline-sync`;

    if (!context.restaurant_id) {
      throw new Error(context.error || 'No hay restaurante activo para sincronizar acciones pendientes.');
    }

    recovered = await recoverStaleProcessingActions(context.restaurant_id, {
      processingOwner,
    });

    const queuedActions = (await listPendingActions(context.restaurant_id))
      .filter((action) => [
        PENDING_ACTION_STATUS.PENDING,
        PENDING_ACTION_STATUS.FAILED,
      ].includes(action.status))
      .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));

    ({
      syncableActions: actions,
      deferredDraftActionIds,
    } = buildSyncableActions(queuedActions));

    if (!actions.length) {
      const deferredMessage = deferredDraftActionIds.length
        ? `Hay ${deferredDraftActionIds.length} accion(es) de borradores locales pendientes de confirmacion.`
        : 'No hay acciones pendientes.';
      await saveSyncStatus({
        restaurant_id: context.restaurant_id,
        status: 'success',
        message: deferredMessage,
        completed: 0,
        failed: 0,
        recovered_processing: recovered.recovered,
        deferred_draft_actions: deferredDraftActionIds.length,
      });
      return {
        status: 'success',
        completed: 0,
        failed: 0,
        recovered_processing: recovered.recovered,
        deferred_draft_actions: deferredDraftActionIds.length,
      };
    }

    await Promise.all(actions.map((action) => markPendingActionProcessing(action.id, processingOwner)));

    const response = await axios.post(`${apiBaseUrl()}${SYNC_ENDPOINT}`, {
      restaurant_id: context.restaurant_id,
      device_id: getOrCreateDeviceId(),
      actions: actions.map((action) => ({
        ...action,
        idempotency_key: action.idempotency_key || action.id,
        client_request_id: action.client_request_id || action.id,
      })),
    }, {
      headers: authHeaders(),
      timeout: 20000,
    });

    const completedIds = response.data?.completed_action_ids || [];
    const actionErrors = response.data?.action_errors || {};
    const serverIdMappings = response.data?.server_id_mappings || [];
    const errorSummary = countActionErrors(actionErrors);

    await Promise.all(serverIdMappings
      .filter((mapping) => mapping.entity_type === 'service' && mapping.local_id)
      .map((mapping) => markLocalServiceSynced(context.restaurant_id, mapping.local_id, mapping.server_id))
    );
    await Promise.all(serverIdMappings
      .filter((mapping) => mapping.entity_type === 'order' && mapping.local_id)
      .map((mapping) => markLocalOrderSynced(context.restaurant_id, mapping.local_id, mapping.server_id))
    );
    await Promise.all(serverIdMappings
      .filter((mapping) => mapping.entity_type === 'order_line' && mapping.local_id)
      .map((mapping) => markLocalOrderLineSynced(
        context.restaurant_id,
        mapping.local_id,
        mapping.server_id,
        mapping.order_server_id || null,
      ))
    );
    await Promise.all(serverIdMappings
      .filter((mapping) => mapping.entity_type === 'payment' && mapping.local_id)
      .map((mapping) => markLocalPaymentSynced(context.restaurant_id, mapping.local_id, mapping.server_id))
    );

    await Promise.all(completedIds.map((actionId) => markPendingActionCompleted(actionId)));
    await Promise.all(Object.entries(actionErrors).map(([actionId, error]) =>
      markPendingActionFailed(actionId, normalizeActionError(error))
    ));

    const failed = errorSummary.failed;
    await saveSyncStatus({
      restaurant_id: context.restaurant_id,
      status: failed ? 'partial_error' : 'success',
      message: failed ? 'Algunos cambios no se pudieron sincronizar.' : 'Sincronización completada.',
      completed: completedIds.length,
      failed,
      conflicts: errorSummary.conflicts,
      policy_blocked: errorSummary.policy_blocked,
      recovered_processing: recovered.recovered,
      deferred_draft_actions: deferredDraftActionIds.length,
      conflict_action_ids: response.data?.conflict_action_ids || [],
      policy_blocked_action_ids: response.data?.policy_blocked_action_ids || [],
    });

    markBackendAvailable();

    return {
      status: failed ? 'partial_error' : 'success',
      completed: completedIds.length,
      failed,
      conflicts: errorSummary.conflicts,
      policy_blocked: errorSummary.policy_blocked,
      recovered_processing: recovered.recovered,
      deferred_draft_actions: deferredDraftActionIds.length,
    };
  } catch (error) {
    if (isBackendUnavailableError(error)) {
      markBackendUnavailable(error?.message || 'No se pudo sincronizar con backend.');
    }

    const backendMissing = [404, 405, 501].includes(Number(error?.response?.status));
    const message = backendMissing
      ? `Backend pendiente: implementar POST ${SYNC_ENDPOINT} con idempotency_key/client_request_id.`
      : (error?.message || 'No se pudieron sincronizar acciones pendientes.');

    const markAfterSyncError = isBackendUnavailableError(error)
      ? (action) => markPendingActionPending(action.id, { last_error: message })
      : (action) => markPendingActionFailed(action.id, message);
    await Promise.all(actions.map(markAfterSyncError));
    if (context?.restaurant_id) {
      await saveSyncStatus({
        restaurant_id: context.restaurant_id,
        status: 'error',
        message,
        completed: 0,
        failed: actions.length,
        conflicts: 0,
        policy_blocked: 0,
        recovered_processing: recovered.recovered,
        deferred_draft_actions: deferredDraftActionIds.length,
      });
    }

    return {
      status: 'error',
      completed: 0,
      failed: actions.length,
      error: message,
      recovered_processing: recovered.recovered,
      deferred_draft_actions: deferredDraftActionIds.length,
    };
  } finally {
    syncInProgress = false;
  }
}
